import { useReducer, useEffect, useCallback, useRef } from 'react';
import {
  adaptExternalLookupTypeToExternalLookupTypeResponse,
  adaptGetExternalLookupResponseToExternalLookup,
} from '@/adapters';
import { LookupApi } from '@/api';

import { IExternalLookup } from '@/typings';
import {
  clearExternalLookupSuccess,
  fetchExternalLookupSuccess,
  selectLookup,
} from '@/store/slices';
import { useAppDispatch, useAppSelector } from '.';
import { GetExternalLookupRequestDTO } from '@/api/lookup/DTOs';

type State = {
  error: string | null;
  loading: boolean;
};

enum ActionType {
  FETCH_INIT = 'FETCH_INIT',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
}

type Action =
  | { type: ActionType.FETCH_INIT }
  | { type: ActionType.FETCH_SUCCESS }
  | { type: ActionType.FETCH_FAILURE; payload: string };

const dataFetchReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.FETCH_INIT:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case ActionType.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
      };
    case ActionType.FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    default:
      return { ...state };
  }
};

const initialState: State = {
  error: null,
  loading: false,
};

export const useFetchExternalLookup = (
  fieldName: string,
  value?: string,
): {
  error: string | null;
  loading: boolean;
  lookup: IExternalLookup | null;
} => {
  const [state, dispatch] = useReducer(dataFetchReducer, initialState);
  const appDispatch = useAppDispatch();
  const fetchRef = useRef<string | undefined>();

  const { externalLookups } = useAppSelector(selectLookup);

  const externalLookup: IExternalLookup | undefined =
    externalLookups?.[fieldName];

  const clearExternalLookup = useCallback(() => {
    dispatch({ type: ActionType.FETCH_INIT });
    appDispatch(clearExternalLookupSuccess({ fieldName }));
    dispatch({ type: ActionType.FETCH_SUCCESS });
  }, [appDispatch, fieldName]);

  const fetchExternalLookup = useCallback(
    async (options: GetExternalLookupRequestDTO) => {
      dispatch({ type: ActionType.FETCH_INIT });

      const result = await new LookupApi().getExternalLookup(options);

      if (result.success) {
        const data = adaptGetExternalLookupResponseToExternalLookup(
          result.data,
        );

        appDispatch(fetchExternalLookupSuccess({ fieldName, values: data }));

        dispatch({ type: ActionType.FETCH_SUCCESS });
      } else {
        console.error('Failed to fetch external lookup:', result.error);
        dispatch({ type: ActionType.FETCH_FAILURE, payload: result.error.message });
      }
    },
    [dispatch, appDispatch, fieldName],
  );

  useEffect(() => {
    if (externalLookup && value !== undefined && value !== fetchRef.current) {
      fetchRef.current = value;
      if (value === '') {
        clearExternalLookup();
      } else {
        const options: GetExternalLookupRequestDTO = {
          lookupType: adaptExternalLookupTypeToExternalLookupTypeResponse(
            externalLookup.options.lookupType,
          ),
          value,
          additionalLookupArguments:
            externalLookup.options.additionalLookupArguments,
        };
        fetchExternalLookup(options);
      }
    }
  }, [externalLookup, value, fetchExternalLookup, clearExternalLookup]);

  return { ...state, lookup: externalLookup ?? null };
};
